import type { LineaProrrateo } from "@/features/prorrateo/types";
import { TOLERANCIA_PRORRATEO, round2 } from "./suma";

/** Un concepto agrupado con sus sub-líneas y el estado de su cuadre. */
export interface ConceptoGrupo {
  conceptoNumero: number;
  claveSat: string;
  claveSatDescripcion: string;
  descripcion: string;
  /** Importe objetivo inmutable del concepto (las sub-líneas deben sumar esto). */
  importeConcepto: number;
  sublineas: LineaProrrateo[];
  /** Suma actual de las sub-líneas. */
  sumaActual: number;
  /** suma − objetivo (positivo = sobra, negativo = falta). */
  diferencia: number;
  cuadra: boolean;
  /** Alguna sub-línea del concepto está en 0 (no confirmable). */
  tieneCero: boolean;
}

/**
 * Agrupa las sub-líneas por concepto (ordenadas por número de línea) y calcula el
 * cuadre de cada concepto contra su importe objetivo (tolerancia ±$0.01).
 */
export function agruparPorConcepto(lineas: LineaProrrateo[]): ConceptoGrupo[] {
  const porConcepto = new Map<number, LineaProrrateo[]>();
  for (const l of lineas) {
    const arr = porConcepto.get(l.conceptoNumero) ?? [];
    arr.push(l);
    porConcepto.set(l.conceptoNumero, arr);
  }

  return [...porConcepto.entries()]
    .sort(([a], [b]) => a - b)
    .map(([conceptoNumero, subs]) => {
      const sublineas = [...subs].sort((a, b) => a.numeroLinea - b.numeroLinea);
      const base = sublineas[0];
      const sumaActual = round2(sublineas.reduce((acc, l) => acc + l.importe, 0));
      const importeConcepto = base.importeConcepto;
      const diferencia = round2(sumaActual - importeConcepto);
      return {
        conceptoNumero,
        claveSat: base.claveSat,
        claveSatDescripcion: base.claveSatDescripcion,
        descripcion: base.descripcion,
        importeConcepto,
        sublineas,
        sumaActual,
        diferencia,
        cuadra: Math.abs(diferencia) <= TOLERANCIA_PRORRATEO,
        tieneCero: sublineas.some((l) => l.importe <= 0),
      };
    });
}

/**
 * ¿Se puede confirmar? Cada sub-línea con CeCo y cuenta, ninguna en 0, y cada
 * concepto cuadrando contra su importe. Devuelve el primer motivo que lo impide.
 */
export function evaluarConfirmable(
  lineas: LineaProrrateo[],
  /** Claves del catálogo SAT · solo prorrateos de CFDI (D-9): una clave ausente bloquea. */
  catalogoClavesSat?: ReadonlySet<string>
): { ok: boolean; motivo?: string } {
  if (lineas.length === 0) return { ok: false, motivo: "El prorrateo no tiene líneas." };
  if (catalogoClavesSat && lineas.some((l) => l.claveSat?.trim() && !catalogoClavesSat.has(l.claveSat.trim())))
    return { ok: false, motivo: "Alguna clave producto/servicio del CFDI no está en el catálogo de claves SAT · solicita su alta." };
  if (lineas.some((l) => !l.centroDeCostoCodigo))
    return { ok: false, motivo: "Asigna el centro de costo en todas las sub-líneas." };
  if (lineas.some((l) => !l.cuentaContableCodigo))
    return { ok: false, motivo: "Alguna clave no tiene cuenta contable · pide al admin que la agregue, o asígnala en la sub-línea." };
  if (lineas.some((l) => l.importe <= 0))
    return { ok: false, motivo: "Hay sub-líneas en cero · asígnales importe o elimínalas." };
  const grupos = agruparPorConcepto(lineas);
  const noCuadra = grupos.find((g) => !g.cuadra);
  if (noCuadra)
    return {
      ok: false,
      motivo: `El concepto ${noCuadra.conceptoNumero} suma $${noCuadra.sumaActual.toFixed(2)} y debe sumar $${noCuadra.importeConcepto.toFixed(2)}.`,
    };
  return { ok: true };
}

/**
 * D-9 · una línea por clave producto/servicio del CFDI que NO está en el catálogo de claves SAT
 * (las vacías no cuentan · se compara sin espacios). Bloquean la confirmación del prorrateo.
 */
export function clavesNoCatalogadas(lineas: LineaProrrateo[], catalogo: ReadonlySet<string>): LineaProrrateo[] {
  return Array.from(
    new Map(
      lineas
        .filter((l) => l.claveSat?.trim() && !catalogo.has(l.claveSat.trim()))
        .map((l) => [l.claveSat.trim(), l])
    ).values()
  );
}
